import axios, { type AxiosInstance } from 'axios';
import { apiConfig } from '@/config/apiConfig';
import { clearInviteToken, getInviteToken } from './inviteAuth';

/**
 * Standalone axios instance for the candidate-facing invite flow.
 *
 * Critical: this client is intentionally NOT shared with the backoffice
 * `apiClient`. The backoffice client adds the FusionAuth bearer and redirects
 * to `/login` on 401 — both behaviors are wrong for the candidate flow:
 *   - The candidate authenticates with `Authorization: Bearer <invite_token>`
 *     **plus** the `x-auth-type: invite-token` header.
 *   - On 401 we want to bounce the candidate to the expired page, not the
 *     backoffice login.
 */
const inviteApiClient: AxiosInstance = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

inviteApiClient.interceptors.request.use((config) => {
  const token = getInviteToken();
  if (token) {
    config.headers = config.headers || {};
    config.headers['Authorization'] = `Bearer ${token}`;
    config.headers['x-auth-type'] = 'invite-token';
  }
  return config;
});

let onUnauthorized: (() => void) | null = null;

/**
 * Register a single global handler for invite-token 401 responses.
 * The portal layout sets this up to redirect to the expired screen.
 */
export const setInviteUnauthorizedHandler = (handler: (() => void) | null): void => {
  onUnauthorized = handler;
};

inviteApiClient.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error?.response?.status;
    if (status === 401) {
      // Token is invalid / expired — drop it so subsequent calls don't loop.
      clearInviteToken();
      if (onUnauthorized) onUnauthorized();
    }
    return Promise.reject(error);
  },
);

export default inviteApiClient;
